
import React, { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

interface GalleryProps {
  className?: string;
}

// These images should represent different services
const galleryImages = [
  {
    id: 1,
    src: '/lovable-uploads/98c54ed9-ad98-4737-9c44-2f7967c22cc7.png',
    alt: 'Tonte de pelouse professionnelle',
    category: 'Tonte de pelouse'
  },
  {
    id: 2,
    src: '/lovable-uploads/4d4550a3-8c7a-4d2f-a660-b9d322ef85ea.png',
    alt: 'Ménage d\'automne',
    category: 'Ménage d\'automne'
  },
  {
    id: 3,
    src: '/lovable-uploads/b0b1beb8-b76c-40e5-b40a-ff68b25f5cb8.png',
    alt: 'Ouverture de terrain',
    category: 'Ouverture de terrain'
  },
  {
    id: 4,
    src: '/lovable-uploads/7ee3b526-3413-462f-9bcc-9fa2d731a747.png',
    alt: 'Déneigement de toiture',
    category: 'Déneigement de toiture'
  }
];

const Gallery: React.FC<GalleryProps> = ({ className }) => {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descRef = useRef<HTMLParagraphElement>(null);
  const imagesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    
    [titleRef.current, descRef.current, imagesRef.current].forEach((el) => {
      if (el) observer.observe(el);
    });
    
    return () => {
      [titleRef.current, descRef.current, imagesRef.current].forEach((el) => {
        if (el) observer.unobserve(el);
      });
    };
  }, []);

  return (
    <section ref={sectionRef} className={cn("section py-20 bg-white", className)}>
      <div className="container-wide">
        <div className="text-center mb-12">
          <h2 ref={titleRef} className="section-title reveal-on-scroll">Nos Réalisations</h2>
          <p ref={descRef} className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto reveal-on-scroll">
            Découvrez notre portfolio de transformations paysagères réussies pour des clients résidentiels et commerciaux.
          </p>
        </div>

        <div 
          ref={imagesRef} 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 reveal-on-scroll"
        >
          {galleryImages.map((image) => (
            <div 
              key={image.id} 
              className="group relative overflow-hidden rounded-lg shadow-md h-80 image-gallery-item cursor-pointer"
              onClick={() => navigate('/gallery')}
            >
              <img 
                src={image.src} 
                alt={image.alt} 
                className="w-full h-full object-cover service-image"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-end p-6 text-white">
                <span className="bg-brand-green px-3 py-1 rounded text-sm mb-2">
                  {image.category}
                </span>
                <h3 className="font-display text-xl">{image.alt}</h3>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <Button 
            onClick={() => navigate('/gallery')} 
            size="lg"
            className="button-outline-dark-green"
          >
            Voir toute la galerie
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Gallery;
